import React, { useState } from 'react';
import { authPost } from '../lib/authFetch';
import './PlanSelection.css';

function formatPrice(amount, currency) {
  if (amount == null) return '';
  const symbol = (currency || 'usd').toUpperCase() === 'USD' ? '$' : `${(currency || '').toUpperCase()} `;
  const whole = amount % 100 === 0;
  const value = whole ? (amount / 100).toFixed(0) : (amount / 100).toFixed(2);
  return `${symbol}${value}`;
}

function PlanSelection({ user, onComplete, onSignOut, onBack }) {
  const [loading, setLoading] = useState(false);
  const [code, setCode] = useState('');
  const [codeChecking, setCodeChecking] = useState(false);
  const [codeApplied, setCodeApplied] = useState(false);
  const [codeError, setCodeError] = useState('');
  const [priceInfo, setPriceInfo] = useState(null);

  const handleApplyCode = async () => {
    const trimmed = code.trim();
    if (!trimmed) return;
    setCodeChecking(true);
    setCodeError('');
    try {
      const response = await authPost('/api/redeem-code', { code: trimmed });
      const data = await response.json();
      if (data.valid) {
        setCodeApplied(true);
        setPriceInfo(data);
      } else {
        setCodeApplied(false);
        setPriceInfo(null);
        setCodeError(data.error || 'Invalid code');
      }
    } catch (err) {
      console.error('Error redeeming code:', err);
      setCodeApplied(false);
      setPriceInfo(null);
      setCodeError('Failed to check code. Please try again.');
    } finally {
      setCodeChecking(false);
    }
  };

  const handleSubscribe = async () => {
    setLoading(true);
    try {
      const response = await authPost('/api/create-checkout-session', {
        ...(codeApplied ? { code: code.trim() } : {})
      });
      const data = await response.json();
      if (data.url) {
        // Store user ID before redirecting to Stripe
        localStorage.setItem('penelope-checkout-user', user.id);
        window.location.href = data.url;
      }
    } catch (error) {
      console.error('Error creating checkout session:', error);
      setLoading(false);
    }
  };

  return (
    <div className="plan-selection-page">
      {onBack && (
        <button className="plan-back-button" onClick={onBack}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
        </button>
      )}
      <div className="plan-selection-container">
        <h1 className="plan-selection-title">Upgrade to Knowroom Solo</h1>
        <p className="plan-selection-subtitle">
          Get full access to Knowroom with all your data intact.
        </p>

        <div className="plan-card">
          <div className="plan-header">
            <h2 className="plan-name">Solo</h2>
            {codeApplied && priceInfo ? (
              <div className="plan-price">
                <span className="price-amount">{formatPrice(priceInfo.amount, priceInfo.currency)}</span>
                <span className="price-period">/{priceInfo.interval || 'month'}</span>
              </div>
            ) : (
              <p className="plan-price-lead">Enter your code below to see your price</p>
            )}
          </div>
          <ul className="plan-features">
            <li>Unlimited people</li>
            <li>AI-powered insights</li>
            <li>Unlimited lists & tables</li>
            <li>Document uploads</li>
            <li>Priority support</li>
          </ul>

          <div className="plan-code-row">
            <input
              type="text"
              className="plan-code-input"
              placeholder="Enter your code"
              value={code}
              onChange={(e) => {
                setCode(e.target.value);
                setCodeApplied(false);
                setPriceInfo(null);
                setCodeError('');
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleApplyCode();
                }
              }}
              disabled={codeApplied}
            />
            <button
              type="button"
              className="plan-code-apply"
              onClick={handleApplyCode}
              disabled={codeChecking || codeApplied || !code.trim()}
            >
              {codeApplied ? 'Applied' : codeChecking ? 'Checking...' : 'Apply'}
            </button>
          </div>
          {codeError && <p className="plan-code-error">{codeError}</p>}

          <button
            className="plan-button plan-button-primary"
            onClick={handleSubscribe}
            disabled={loading || !codeApplied}
          >
            {loading ? 'Loading...' : 'Subscribe to Solo'}
          </button>

          {onBack && (
            <button
              className="plan-button plan-button-secondary"
              onClick={onBack}
            >
              Maybe later
            </button>
          )}
        </div>

        <p className="plan-selection-footer">
          Questions? <a href="mailto:support@rubin.app">Contact us</a>
          {onSignOut && (
            <>
              {' · '}
              <button onClick={onSignOut} className="plan-selection-signout">Sign out</button>
            </>
          )}
        </p>
      </div>
    </div>
  );
}

export default PlanSelection;
